// src/stores/usePaymentStore.ts
// Payment modal state — single source of truth for modal visibility and views.
// Subscription truth lives in useAuthStore.profile.subscription.
// In-memory subscription & pages-URL caches live in paymentService.ts.

import { create } from 'zustand';

// ── Public types ─────────────────────────────────────────────────────────────

export type PlanId = 'monthly' | 'three_months' | 'yearly';

export type PaymentView = 'main' | 'loading' | 'success' | 'error' | 'cancel';

// ── Store interface ──────────────────────────────────────────────────────────

interface PaymentState {
  // Modal visibility
  isOpen: boolean;
  // Which screen is currently rendered inside the modal
  view: PaymentView;
  // The plan ID the user has selected (null = none chosen yet)
  selectedPlan: PlanId | null;
  // Populated when a 100%-off promo activates or after BOG return
  successPlan: PlanId | null;
  successExpiry: string | null;
  // Error screen copy
  errorTitle: string;
  errorMessage: string;

  // ── Actions ────────────────────────────────────────────────────────────
  openModal: () => void;
  closeModal: () => void;
  setView: (view: PaymentView) => void;
  setSelectedPlan: (plan: PlanId | null) => void;
  setSuccessInfo: (plan: PlanId, expiry: string) => void;
  setError: (title: string, message: string) => void;
  /** Resets all transient modal state (view, selection, promo feedback). */
  resetModal: () => void;
}

// ── Default error copy ────────────────────────────────────────────────────────

const DEFAULT_ERROR_TITLE   = 'Something went wrong';
const DEFAULT_ERROR_MESSAGE = 'An unexpected error occurred. Please try again or contact support.';

// ── Store ────────────────────────────────────────────────────────────────────

export const usePaymentStore = create<PaymentState>()((set) => ({
  isOpen: false,
  view: 'main',
  selectedPlan: null,
  successPlan: null,
  successExpiry: null,
  errorTitle: DEFAULT_ERROR_TITLE,
  errorMessage: DEFAULT_ERROR_MESSAGE,

  openModal: () => set({ isOpen: true, view: 'main' }),
  closeModal: () => set({ isOpen: false }),

  setView: (view) => set({ view }),
  setSelectedPlan: (plan) => set({ selectedPlan: plan }),
  setSuccessInfo: (plan, expiry) => set({ successPlan: plan, successExpiry: expiry }),
  setError: (title, message) => set({ errorTitle: title, errorMessage: message }),

  resetModal: () =>
    set({
      view: 'main',
      selectedPlan: null,
      successPlan: null,
      successExpiry: null,
      errorTitle: DEFAULT_ERROR_TITLE,
      errorMessage: DEFAULT_ERROR_MESSAGE,
    }),
}));